/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Suspense, useState } from 'react';
import Card from '@/components/card';
import Title from '@/components/title';
import { InboundMenu } from '@/configs/menuItems';
import { getPathSegment } from '@/utils/pathUtils';
import SubMenu from '@/components/menu/sub-menu';
import InboundTable from '@/containers/inbound/components/inbound-table';
import { EInboundStatus, EInboundType } from '@/enums/EInbound';
import {
  IInbound,
  ISearchInboundReq,
  ISearchInboundRes,
} from '@/interfaces/inbound';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { searchInbounds } from '@/adapters/inbound';
import { DatePicker, TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import Search from 'antd/es/input/Search';
import locale from 'antd/es/date-picker/locale/th_TH';
import dayjs, { Dayjs } from 'dayjs';
import CostCalculationModal from './components/cost-calculation-modal';
import { IDataModal } from '@/interfaces/dataModal';

const _criteria: ISearchInboundReq = {
  pageSize: 10,
  pageStart: 1,
  startDate: dayjs().startOf('month').toDate(),
  endDate: dayjs().endOf('month').toDate(),
  status: EInboundStatus.COMPLETED,
  type: EInboundType.NORMAL,
};

const _dataModal: IDataModal<IInbound> = {
  open: false,
};

const FinancialReport: React.FC = () => {
  const pathSegment = getPathSegment();
  const [criteria, setCriteria] = useState<ISearchInboundReq>({
    ..._criteria,
  });

  const [dataModal, setDataModal] = useState<IDataModal<IInbound>>({
    ..._dataModal,
  });

  const { data, isLoading, isFetching } = useQuery<
    AxiosResponse<ISearchInboundRes>,
    Error
  >({
    queryKey: ['search-inbound', criteria],
    queryFn: async () => await searchInbounds(criteria),
    enabled: criteria?.status !== EInboundStatus.ERROR,
  });

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IInbound> | SorterResult<IInbound>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof IInbound) || newCriteria.orderBy;
      newCriteria.orderType = sorter.order || newCriteria.orderType;
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      pageStart: 1,
      pageSize: 10,
    });
  };

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      setCriteria({
        ...criteria,
        pageStart: 1,
        pageSize: 10,
        startDate: dates[0]?.toDate(),
        endDate: dates[1]?.toDate(),
      });
    } else {
      setCriteria({
        ...criteria,
        pageStart: 1,
        pageSize: 10,
        startDate: null,
        endDate: null,
      });
    }
  };

  return (
    <>
      <SubMenu active={pathSegment} items={InboundMenu} />
      <Card customClass="min-h-[200px]">
        <Title title="Financial Report" />
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <div className="flex flex-col md:flex-row justify-between gap-3">
              <DatePicker.RangePicker
                defaultValue={[
                  dayjs().startOf('month'),
                  dayjs().endOf('month'),
                ]}
                size="large"
                locale={locale}
                onChange={onRangeChange}
              />
              <Search
                className="md:max-w-[400px] md:min-w-[400px]"
                placeholder="ค้นหา"
                allowClear
                size="large"
                enterButton="ค้นหา"
                onSearch={onSearch}
              />
            </div>
          </div>
          <InboundTable
            isFinance
            criteria={criteria}
            inbound={data?.data || { data: [], totalRecords: 0 }}
            loading={isLoading || isFetching}
            onEditData={item => setDataModal({ open: true, item: item })}
            onDelete={() => {
              console.log();
            }}
            onTableChange={onTableChange}
          />
          <Suspense>
            <CostCalculationModal
              dataModal={dataModal}
              handleOk={function (): void {
                setDataModal({
                  ..._dataModal,
                });
              }}
              handleCancel={function (): void {
                setDataModal({
                  ..._dataModal,
                });
              }}
            />
          </Suspense>
        </div>
      </Card>
    </>
  );
};

export default FinancialReport;
